import React from "react";
import {
  Box,
  Typography,
} from "@mui/material";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import StarIcon from "@mui/icons-material/Star";
import RestaurantMenuIcon from "@mui/icons-material/RestaurantMenu";
import { useProductsOffers } from "../../../../../../../context/OrderCreator/ProductContext";

const CategorySelector = ({ kitchen = {}, selectedCategory, onCategorySelect, orderId }) => {
  const [showOffers, setShowOffers] = React.useState(false);
  const [showFavorites, setShowFavorites] = React.useState(false);
  const { favorites = [], favoritesLoading } = useProductsOffers();

  // Normalize kitchen.categories into an array safely
  const rawCategories = kitchen?.categories ?? [];
  const kitchenCategories = Array.isArray(rawCategories)
    ? rawCategories
    : rawCategories && typeof rawCategories === "object"
    ? Object.values(rawCategories)
    : [];

  // Filter out categories whose status is 0 (hide them)
  const filteredKitchenCategories = (kitchenCategories || []).filter((c) => {
    if (c == null) return false;
    const status = c.status;
    if (status === undefined || status === null) return true;
    return Number(status) !== 0;
  });

  // إضافة عنصر "العروض" في البداية
  const categories = [
    { id: "offers", name: "العروض", products: [] },
    { id: "favorites", name: "المفضلة", products: [] },
    ...filteredKitchenCategories,
  ];

  const handleCategoryClick = (cat) => {
    if (cat.id === "offers") {
      setShowOffers(true);
      setShowFavorites(false);
      onCategorySelect(cat); // اجعل تصنيف العروض نشطًا أيضًا
    } else {
      if (cat.id === "favorites") {
        setShowFavorites(true);
        setShowOffers(false);
        onCategorySelect(cat);
      } else {
        setShowOffers(false);
        setShowFavorites(false);
        onCategorySelect(cat);
      }
    }
  };

  return (
    <>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(110px, 1fr))",
          gap: 1.5,
          mb: 2,
          maxWidth: "100%",
        }}
      >
        {categories.map((cat) => {
          const isActive =
            selectedCategory?.id === cat.id ||
            (cat.id === "offers" && showOffers) ||
            (cat.id === "favorites" && showFavorites);
          
          const Icon =
            cat.id === "offers"
              ? LocalOfferIcon
              : cat.id === "favorites"
              ? StarIcon
              : RestaurantMenuIcon;

          return (
            <Box
              key={cat.id}
              onClick={() => handleCategoryClick(cat)}
              sx={{
                width: "100%",
                p: 0,
                borderRadius: 3,
                textAlign: "center",
                cursor: "pointer",
                background: isActive
                  ? "linear-gradient(135deg, rgba(251, 194, 235, 0.15) 0%, rgba(26, 26, 46, 0.95) 100%)"
                  : "linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)",
                backdropFilter: "blur(10px)",
                color: "#fff",
                border: isActive
                  ? "2px solid rgba(251, 194, 235, 0.8)"
                  : "1.5px solid rgba(255, 255, 255, 0.08)",
                boxShadow: isActive
                  ? "0 0 0 3px rgba(251, 194, 235, 0.2), 0 8px 24px rgba(251, 194, 235, 0.3)"
                  : "0 4px 12px rgba(0, 0, 0, 0.2)",
                position: "relative",
                transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                overflow: "hidden",
                minHeight: 85,
                "&:hover": {
                  border: "2px solid rgba(251, 194, 235, 0.6)",
                  transform: "translateY(-4px) scale(1.02)",
                  boxShadow: "0 12px 32px rgba(251, 194, 235, 0.25)",
                  background: "linear-gradient(135deg, rgba(251, 194, 235, 0.2) 0%, rgba(26, 26, 46, 0.95) 100%)",
                },
                "&::before": {
                  content: '""',
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  height: "3px",
                  background: isActive
                    ? "linear-gradient(90deg, #fbc2eb 0%, #ff9a9e 100%)"
                    : "transparent",
                  transition: "all 0.3s ease",
                },
              }}
            >
              <Box
                sx={{
                  p: 1.5,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 0.5,
                }}
              >
                <Box
                  sx={{
                    width: 34,
                    height: 34,
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    background: isActive
                      ? "linear-gradient(135deg, #fbc2eb 0%, #ff9a9e 100%)"
                      : "rgba(255, 255, 255, 0.06)",
                    transition: "all 0.3s ease",
                    boxShadow: isActive ? "0 4px 12px rgba(251, 194, 235, 0.4)" : "none",
                  }}
                >
                  <Icon sx={{ fontSize: 20, color: isActive ? "#000" : "#fbc2eb" }} />
                </Box>
                <Typography
                  sx={{
                    fontWeight: 700,
                    fontSize: 13,
                    letterSpacing: "0.3px",
                    overflowWrap: "break-word",
                    wordWrap: "break-word",
                    whiteSpace: "normal",
                    color: isActive ? "#fbc2eb" : "#fff",
                    textShadow: "0 2px 8px rgba(0,0,0,0.4)",
                    transition: "all 0.3s ease",
                  }}
                >
                  {cat.name}
                </Typography>
                <Typography
                  sx={{
                    color: isActive ? "rgba(251, 194, 235, 0.9)" : "rgba(255, 255, 255, 0.7)",
                    fontSize: 10,
                    fontWeight: 500,
                    transition: "all 0.3s ease",
                  }}
                >
                  {cat.id === "offers"
                    ? "العروض المتاحة"
                    : cat.id === "favorites"
                    ? favoritesLoading
                      ? "جاري التحميل..."
                      : `${favorites.length} عنصر`
                    : `${(cat.products || []).length} عدد الاصناف`}
                </Typography>
              </Box>
            </Box>
          );
        })}
      </Box>
    </>
  );
};

export default CategorySelector;
